import React, { useEffect, useMemo, useRef } from 'react';
import { ViewStyle, TextStyle, Platform } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import type { RootStackParamList } from '@/navigation/RootNavigator';
import { RNButton } from '@/components';
import { useEntrataIntegrationDisabled } from '@/features/payments/hooks/useEntrataIntegrationDisabled';
import { useLazyGetLeaseQuery } from '@/store/api';
import { useLazyGetPaymentMethodsQuery, useLazyGetFeeStructureQuery } from '@/store/api';
import { useAppDispatch } from '@/store';
import { useResponsive } from '@/hooks/common/useResponsive';
import {
  setPayingForLeaseId,
  setPayingForResidentId,
  setAssociatedLeaseAccount,
} from '@/store/slices/residents/paymentsSlice';

interface Props {
  leaseId: number;
  residentId?: number;
  leaseUser?: any;
  variant?: 'pink' | 'clearPink' | 'primary' | 'secondary' | 'link' | 'outlined';
  style?: ViewStyle;
  buttonStyle?: ViewStyle;
  textStyle?: TextStyle;
}

export const MakePaymentBtn: React.FC<Props> = ({
  leaseId,
  residentId,
  leaseUser,
  variant = 'primary',
  style,
  buttonStyle,
  textStyle,
}) => {
  const navigation = useNavigation<StackNavigationProp<RootStackParamList>>();
  const { layoutMode } = useResponsive();
  const isMobile = layoutMode !== 'desktop';
  const entrataDisabled = useEntrataIntegrationDisabled();
  const dispatch = useAppDispatch();

  // Skip balance prefetch to avoid Entrata errors - let payment modal handle data loading
  const isFetching = false;

  // Prefetch methods and fees similar to web before opening the wizard
  const [prefetchPaymentMethods, paymentMethodsResult] = useLazyGetPaymentMethodsQuery();
  const [prefetchFeeStructure, feeStructureResult] = useLazyGetFeeStructureQuery();
  const isWarmupFetching = paymentMethodsResult.isFetching || feeStructureResult.isFetching;
  const isWarmupSuccess = paymentMethodsResult.isSuccess && feeStructureResult.isSuccess;
  const hasWarmupStartedRef = useRef(false);

  useEffect(() => {
    if (!hasWarmupStartedRef.current && !entrataDisabled) {
      hasWarmupStartedRef.current = true;
      prefetchPaymentMethods(undefined, true);
      prefetchFeeStructure(undefined, true);
    }
  }, [prefetchPaymentMethods, prefetchFeeStructure, entrataDisabled]);

  const onPress = () => {
    // Mirror web: set payment context before navigation
    if (leaseId) {
      dispatch(setPayingForLeaseId(leaseId));
    }
    const effectiveResidentId = residentId || leaseUser?.id;
    if (effectiveResidentId) {
      dispatch(setPayingForResidentId(effectiveResidentId));
    }
    if (leaseUser) {
      dispatch(
        setAssociatedLeaseAccount({
          id: leaseUser?.id,
          firstName: leaseUser?.firstName,
          lastName: leaseUser?.lastName,
        })
      );
    }

    // On mobile, navigate to full-screen payment flow
    if (isMobile) {
      navigation.navigate('MakePayment', { leaseId });
    } else {
      // On desktop, use modal
      navigation.navigate('PaymentSummaryModal', {
        leaseId,
        residentId: effectiveResidentId,
      } as any);
    }
  };

  // Map to RNButton variants (support link for outlined style)
  const mappedVariant: 'primary' | 'secondary' | 'link' | 'outlined' | 'tertiary' =
    variant === 'outlined'
      ? 'outlined'
      : variant === 'link'
      ? 'link'
      : variant === 'clearPink'
      ? 'secondary'
      : isMobile
      ? 'tertiary'
      : 'primary';

  return (
    <RNButton
      title="Make a payment"
      variant={mappedVariant}
      onPress={onPress}
      style={style || buttonStyle}
      textStyle={textStyle}
      disabled={entrataDisabled}
      loading={isFetching || isWarmupFetching}
    />
  );
};


